import ReactECharts from 'echarts-for-react'
import { useMemo } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { PARTNER360_CHART_FONT_FAMILY } from 'partner360/configs/typography'

export interface IKeyInitiativesBarPoint {
  name: string
  value: number
}

interface IKeyInitiativesBarChartProps {
  /** Already grouped and ordered by the caller — one bar per point. */
  data: IKeyInitiativesBarPoint[]
  /** Formats both the Y axis and the tooltip; returns the raw number for plain counts. */
  formatValue: (value: number) => string | number
  height?: number
  /**
   * Caps an axis label at this many characters, the rest replaced by an ellipsis; the full name
   * stays in the tooltip. Worth setting where the chart's height is fixed — labels are rotated,
   * so a long one eats the plotting area through `containLabel`. Left unset, labels print in
   * full, which is what a full-height dashboard wants.
   *
   * Cutting in the label formatter, the way the MDF breakdown chart does, rather than through
   * `axisLabel.width` + `overflow: 'truncate'` — that pair does not take effect on rotated
   * category labels.
   */
  maxLabelLength?: number
}

const DEFAULT_HEIGHT = 520

/**
 * The initiatives bar chart, presentation only: it takes ready points and draws them.
 *
 * Shared so the Planning activities dashboard (which groups by its own controls form) and the
 * planning cockpit's Initiatives tile (which groups the `planningCockpitGzip` payload) render
 * the same chart from their own data.
 */
export const KeyInitiativesBarChart = ({
  data,
  formatValue,
  height = DEFAULT_HEIGHT,
  maxLabelLength,
}: IKeyInitiativesBarChartProps) => {
  const t = useSafetyTranslation()

  const option = useMemo(
    () => ({
      textStyle: { fontFamily: PARTNER360_CHART_FONT_FAMILY },

      title: {
        left: 0,
        top: 0,
        textStyle: {
          fontSize: 20,
          fontWeight: 700,
          color: '#14284b',
        },
      },

      grid: {
        left: 40,
        right: 24,
        top: 70,
        bottom: 48,
        containLabel: true,
      },

      xAxis: {
        type: 'category',
        data: data.map((item) => t(item.name)),
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: '#d6d6d6',
          },
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          interval: 0,
          rotate: 45,
          formatter: (value: string) =>
            maxLabelLength && value.length > maxLabelLength
              ? `${value.slice(0, maxLabelLength)}...`
              : value,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#666',
          fontSize: 14,
          formatter: formatValue,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#d6d6d6',
          },
        },
        splitLine: {
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: formatValue,
      },

      series: [
        {
          type: 'bar',
          data: data.map((item) => item.value),
          barWidth: '70%',
          itemStyle: {
            color: '#288bd6',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
      dataZoom: [
        {
          show: data.length > 20,
          type: 'slider',
          xAxisEnd: 0,
          start: 0,
          end: data.length > 20 ? (10 / data.length) * 100 : 100,
          orient: 'horizontal',
        },
      ],
    }),
    [data, formatValue, maxLabelLength, t]
  )

  return <ReactECharts option={option} style={{ height, width: '100%' }} />
}

KeyInitiativesBarChart.displayName = 'KeyInitiativesBarChart'
